/* Barcode decoding via zxing-wasm.
 *
 * ---------------------------------------------------------------------------
 * WHY zxing-wasm AND NOT THE NATIVE BarcodeDetector — settled, do not re-open.
 *
 * `BarcodeDetector` is genuinely good on Android Chrome (unflagged since Chrome
 * 83, and its Android GMS backend really does decode DataMatrix). It is not
 * usable here because iOS is a primary target and on iOS Safari the API is
 * FLAG-GATED behind an experimental Feature Flag the user has to flip by hand.
 * A public PWA cannot ask a technician to do that, so a native-first design
 * would have no iOS path at all. WASM-first works everywhere today; the native
 * API is at best an opportunistic Android fast path, which is not worth the
 * second code path to maintain.
 *
 * zxing-wasm's reader build is ~1.04 MiB of WASM (measured: 1,093,289 bytes at
 * v3.1.3) and its format table confirms DataMatrix as well as Code 128 / QR /
 * PDF417 / Aztec. The label's actual symbology is still an open question, so
 * covering the whole plausible set for 1 MiB is the cheap way to not be wrong.
 *
 * OCR IS DEFERRED, deliberately and not as an oversight. Tesseract.js is ~6-7 MB
 * of first-run payload (2.7 MB WASM core + ~2.8 MB gzipped trained data + glue)
 * — six to seven times this entire recognition budget, in an app whose defining
 * constraint is bad connectivity. The fallback for a failed decode is MANUAL
 * ENTRY, not a second recognition engine. Do not add Tesseract.
 * ---------------------------------------------------------------------------
 *
 * The WASM is BUNDLED, not fetched from a CDN. zxing-wasm's default
 * `locateFile` points at jsDelivr, which would make the scanner fail on exactly
 * the sites this app exists for. `loadDecoder` overrides `locateFile` with the
 * Vite-emitted asset URL so the bytes come from our own origin and the service
 * worker can precache them.
 *
 * The whole module is loaded through a dynamic `import()` on first entry into
 * the scan flow, so the 1 MiB never lands in the app shell.
 */

import { candidatesFromFrame, type DecodeCandidate, type DecodedSymbol } from './decodeFilter';

/** What the scan loop needs from a decoder. The only seam that matters here. */
export interface BarcodeDecoder {
  /**
   * Decode one frame. Returns candidates in reading order; [] for no read.
   *
   * Accepts a `Blob` as well as `ImageData` because zxing decodes encoded image
   * files directly (it carries its own image loader). That is what lets a photo
   * picked from the library be scanned, not just a live frame — and it is what
   * makes an end-to-end decode testable off a fixture file.
   */
  decode(frame: ImageData | Blob): Promise<DecodeCandidate[]>;
}

export interface DecoderInit {
  /**
   * Pre-fetched WASM bytes. When supplied, nothing is fetched at all: the
   * module is instantiated straight from these bytes. Useful when the service
   * worker already holds the binary, and it is how the end-to-end test loads it
   * outside a browser.
   */
  wasmBinary?: ArrayBuffer;
}

/**
 * Formats we ask zxing to try.
 *
 * Narrower than "All" on purpose: every extra symbology is per-frame CPU on a
 * mid-range Android, and this list already covers the plausible set for an
 * Amazon-generated or vendor-generated module nameplate. Widen it if and when
 * the real label turns out to be something else — the open question is
 * PRD Q-45, and this constant is the one place to change.
 */
export const SCAN_FORMATS = [
  'Code128',
  'Code39',
  'Code93',
  'DataMatrix',
  'QRCode',
  'PDF417',
  'Aztec',
  'ITF',
  'Codabar',
] as const;

let cached: Promise<BarcodeDecoder> | null = null;

/**
 * Load and configure the decoder. Idempotent — repeated calls share one WASM
 * instance, because instantiating it per scan would stall the first frame of
 * every capture.
 */
export function loadDecoder(init?: DecoderInit): Promise<BarcodeDecoder> {
  cached ??= build(init);
  return cached;
}

/** Test/teardown hook. */
export function resetDecoder(): void {
  cached = null;
}

async function build(init?: DecoderInit): Promise<BarcodeDecoder> {
  const { prepareZXingModule, readBarcodes } = await import('zxing-wasm/reader');

  if (init?.wasmBinary) {
    prepareZXingModule({ overrides: { wasmBinary: init.wasmBinary }, fireImmediately: false });
  } else {
    // Bundled asset, same origin. Without this override zxing-wasm fetches the
    // binary from jsDelivr and the scanner is dead offline. The import is
    // dynamic so that a caller supplying `wasmBinary` never pulls the asset in.
    const { default: wasmUrl } = await import('zxing-wasm/reader/zxing_reader.wasm?url');
    prepareZXingModule({
      overrides: {
        locateFile: (path: string, prefix: string): string =>
          path.endsWith('.wasm') ? wasmUrl : prefix + path,
      },
      fireImmediately: false,
    });
  }

  return {
    async decode(frame: ImageData | Blob): Promise<DecodeCandidate[]> {
      const results = await readBarcodes(frame, {
        formats: [...SCAN_FORMATS],
        // Multiple labels can be in one frame over a pallet.
        maxNumberOfSymbols: 6,
        // A read that fails its own checksum must arrive as an error we can
        // drop, not as a plausible-looking string.
        returnErrors: true,
        tryHarder: true,
        tryRotate: true,
        tryInvert: true,
        tryDownscale: true,
      });
      return candidatesFromFrame(results as unknown as DecodedSymbol[]);
    },
  };
}
